import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Paper,
  Typography,
  styled,
} from "@mui/material";
import { useRef, useState } from "react";
import { MdCloudUpload, MdRestartAlt } from "react-icons/md";
import { read, utils } from "xlsx";

import { EXCEL_HEADERS, EXCEL_SHEETS, STATUS_CODES } from "constants/index";
import { PREPICKING_VALIDATE_URL } from "constants/urls";
import { sanitizeExcelValue, toNumber } from "helpers";
import { validateSchema } from "helpers/schemaValidator";
import useCrud from "hooks/useCrud";
import {
  Article,
  ExcelCellProps,
  PrePickingItemInput,
  PrePickingValidationRequest,
  PrePickingValidationResponse,
} from "types";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Props = {
  clientId: number | null;
  disabled?: boolean;
  articles: Article[];
  onValidated: (response: PrePickingValidationResponse) => void;
};

const inputMimeType =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel";

const normalizeCellString = (value: unknown) => {
  const sanitized = sanitizeExcelValue(
    value as string | number | null | undefined,
  );

  if (sanitized == null) return null;

  const normalized = String(sanitized).trim();
  return normalized === "" ? null : normalized;
};

export default function FileImportPrePicking({
  clientId,
  disabled = false,
  articles,
  onValidated,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [listOfErrors, setListOfErrors] = useState<string[]>([]);
  const [resetInput, setResetInput] = useState(Math.random().toString(36));
  const fileUploadRef = useRef<HTMLInputElement | null>(null);
  const { createData: validatePrePicking } = useCrud<PrePickingValidationRequest>(
    PREPICKING_VALIDATE_URL,
    { disableAutoFetch: true },
  );

  const closeErrorModal = () => {
    setModalOpen(false);
    setListOfErrors([]);
    setResetInput(Math.random().toString(36));
  };

  const openErrors = (errors: string[]) => {
    setListOfErrors(errors);
    setModalOpen(true);
    setLoading(false);
  };

  const buildItems = (
    rows: Record<string, unknown>[],
  ): PrePickingItemInput[] =>
    rows.map((row, index) => ({
      row_number: index + 2,
      article_code:
        normalizeCellString(row[EXCEL_HEADERS.PREPICKING.ARTICULO]) ?? "",
      quantity: toNumber(row[EXCEL_HEADERS.PREPICKING.CANTIDAD] as string | number),
      fecha_ingreso: normalizeCellString(
        row[EXCEL_HEADERS.PREPICKING.FECHA_INGRESO],
      ),
      lote: normalizeCellString(row[EXCEL_HEADERS.PREPICKING.LOTE]),
      oc: normalizeCellString(
        row[EXCEL_HEADERS.PREPICKING.ORDEN_DE_COMPRA],
      ),
      fecha_vencimiento: normalizeCellString(
        row[EXCEL_HEADERS.PREPICKING.FECHA_DE_VENCIMIENTO],
      ),
    }));

  const readFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!clientId) {
      openErrors(["Debe seleccionar un cliente antes de importar el archivo."]);
      return;
    }

    setLoading(true);
    setListOfErrors([]);

    try {
      const file = e.target.files?.[0];

      if (!file) {
        openErrors(["No se seleccionó ningún archivo."]);
        return;
      }

      const reader = new FileReader();

      reader.onerror = () => {
        openErrors(["No se pudo leer el archivo."]);
      };

      reader.onload = async (ev) => {
        try {
          const workbook = read(
            (ev.target as FileReader).result as ArrayBuffer,
            { type: "array" },
          );

          if (
            !workbook.SheetNames[0] ||
            workbook.SheetNames[0] !== EXCEL_SHEETS.PREPICKING
          ) {
            openErrors([
              `La hoja del archivo debe llamarse "${EXCEL_SHEETS.PREPICKING}".`,
            ]);
            return;
          }

          const rows = utils.sheet_to_json(
            workbook.Sheets[workbook.SheetNames[0]],
            {
              defval: "",
              raw: false,
            },
          ) as Record<string, unknown>[];

          if (!rows.length) {
            openErrors(["El archivo no contiene filas para validar."]);
            return;
          }

          const schema: Record<string, ExcelCellProps> = {
            [EXCEL_HEADERS.PREPICKING.ARTICULO]: {
              type: "string",
              required: true,
              associations: articles.map((article) => article.code),
            },
            [EXCEL_HEADERS.PREPICKING.CANTIDAD]: {
              type: "number",
              required: true,
            },
            [EXCEL_HEADERS.PREPICKING.FECHA_INGRESO]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.PREPICKING.LOTE]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.PREPICKING.ORDEN_DE_COMPRA]: {
              type: "string",
              required: false,
            },
            [EXCEL_HEADERS.PREPICKING.FECHA_DE_VENCIMIENTO]: {
              type: "string",
              required: false,
            },
          };

          const validation = validateSchema(rows, schema);

          if (!validation.isValid) {
            openErrors(validation.errors.map((error) => error.message));
            return;
          }

          const payload: PrePickingValidationRequest = {
            client_id: clientId,
            items: buildItems(rows),
          };

          const response = await validatePrePicking(payload, true);

          if (response.status === STATUS_CODES.OK) {
            onValidated(response.data as PrePickingValidationResponse);
            setLoading(false);
            setResetInput(Math.random().toString(36));
            return;
          }

          if (response.status === STATUS_CODES.BAD_REQUEST) {
            const backendErrors =
              (
                response.data?.errors as
                  | { message?: string; msg?: string }[]
                  | undefined
              )?.map(
                (error) =>
                  error.message || error.msg || "Error de validación.",
              ) ?? [response.data?.message || "Archivo inválido."];

            openErrors(backendErrors);
            return;
          }

          openErrors([
            response.data?.message ||
              "Ocurrió un error al validar el pre-picking.",
          ]);
        } catch (error: any) {
          openErrors([
            `Error procesando el Excel: ${error?.message ?? String(error)}`,
          ]);
        } finally {
          setLoading(false);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error: any) {
      openErrors([error?.message ?? "Ocurrió un error inesperado."]);
    }
  };

  return (
    <>
      <Button
        component="label"
        variant="contained"
        startIcon={<MdCloudUpload />}
        disabled={disabled || loading}
      >
        {loading ? <CircularProgress size={20} /> : "Cargar Excel"}
        <VisuallyHiddenInput
          ref={fileUploadRef}
          key={resetInput}
          type="file"
          onChange={readFile}
          accept={inputMimeType}
        />
      </Button>

      <Modal open={modalOpen} onClose={closeErrorModal}>
        <Paper
          sx={{
            position: "fixed",
            left: "50%",
            top: "50%",
            maxHeight: "90vh",
            width: "min(42rem, 92vw)",
            transform: "translate(-50%, -50%)",
            padding: "1rem",
            overflowY: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Ocurrió un error</Typography>
            </Grid>
            <Grid item xs={12}>
              {listOfErrors.map((error, index) => (
                <Alert key={`${error}-${index}`} severity="error" sx={{ mb: 1 }}>
                  {error}
                </Alert>
              ))}
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="flex-end">
              <Button
                onClick={closeErrorModal}
                startIcon={<MdRestartAlt />}
                variant="contained"
              >
                Reintentar
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Modal>

      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          color: "#fff",
          backdropFilter: "blur(2px)",
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
